import { getPersistedExperimentTreatment } from '@ms/yammer-data/dist/domains/experiments/selectors';
import { configureTelemetry } from '@ms/yammer-telemetry';
import { getTelemetryClientConfig, getTelemetryHostingConfig, setTelemetryConfig } from '@ms/yammer-telemetry-store';
import { readHostingConfiguration } from '@ms/yammer-web-support/dist/hosting';

import { persistConfig } from '../../persistedState';

import { getConfig } from './getConfig';
import { getTelemetryClientId } from './getTelemetryClientId';

type SetupTelemetry = (options?: { readonly skipNextCleanup?: boolean }) => Promise<void>;
export const setupTelemetry: SetupTelemetry = async (options) => {
  const config = await getConfig();

  const shouldLogProtobufEventsToConsole = (await getPersistedExperimentTreatment(
    persistConfig.key,
    'LogProtobufEvents'
  )) as boolean;

  const telemetryClientId = await getTelemetryClientId();

  configureTelemetry({
    config,
    telemetryClientId,
    hostingConfig: readHostingConfiguration(),
    registerUnhandledEventsListener: true,
    shouldLogProtobufEventsToConsole,
    enableLongAnimationFrameTimingReporting: true,
    isMsal: true,
    ...options,
  });
};

/**
 * Updates the telemetry config's auth resource scope after it has been
 * initialized. This is needed because setupTelemetry() passes isMsal: true, so
 * configureTelemetry captures config.msal.resources.yammer before the
 * authenticator overrides it to the correct scope (e.g. access_as_user for the
 * new NAA app ids). Without this update, telemetry batch flushes acquire tokens
 * with the stale scope, creating a mismatched MSAL cache entry that keeps the
 * old resource alive and hurts Token Prewarming cache hit rate.
 *
 * Note: the authenticator aliases config.adal = config.msal once it runs, so
 * call sites that pass config.adal.resources.yammer resolve to the same value.
 */
type UpdateTelemetryResource = (resource: string) => void;
export const updateTelemetryResource: UpdateTelemetryResource = (resource) => {
  const clientConfig = getTelemetryClientConfig();
  const hostingConfig = getTelemetryHostingConfig();

  if (!clientConfig || !hostingConfig) {
    return;
  }

  setTelemetryConfig({ ...clientConfig, resource }, hostingConfig);
};
